import React, { Dispatch, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/app-context';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '../../hooks/use-query-params.hook';
import { CustomSelect } from '../../elements/select';
import { CustomInput } from '../../elements/input';
import { CustomButton } from '../../elements/button';
import { Box, Stack } from '@mui/material';
import { LocationDetailsModal } from '../location-details-modal';
import { IOModelVenueWithStores } from '../../types/io-models';
import { ServiceMethod } from '../../types/order';
import { StoreWithDetails } from '../../types/vendor-menu';
import { Section, CollectionPoint, DeliveryArea } from '../../types';
import { IVenueSectionDeliveryInfoMeta } from '../../pages/home/home';
import { STORES_LIST_ROUTE } from '../../constants/routes';

interface IVenueDetailsCaptureFormProps {
  venue: IOModelVenueWithStores;
  serviceAvailable: boolean;
  serviceChoices: {
    serviceMethod: ServiceMethod | undefined;
    deliveryInfo: IVenueSectionDeliveryInfoMeta;
  };
  onLocationSelection: (
    serviceMethod: ServiceMethod | undefined,
    selection: IVenueDetailsCaptureFormProps['serviceChoices']['deliveryInfo'],
    selectedStore?: StoreWithDetails,
  ) => void;
  vendors: StoreWithDetails[];
  refetchVendor: () => void;
  selectedServiceMethod: ServiceMethod | undefined;
  setSelectedServiceMethod: Dispatch<ServiceMethod | undefined>;
}

export const VenueDetailsCaptureForm: React.FC<
  IVenueDetailsCaptureFormProps
> = ({
  venue,
  serviceChoices,
  serviceAvailable,
  onLocationSelection,
  vendors,
  refetchVendor,
  selectedServiceMethod,
  setSelectedServiceMethod,
}) => {
  const { t } = useTranslation(['patron_portal', 'common']);
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const [availableSections, setSections] = useState<Section[]>([]);
  const [availableCollectionPoints, setCollectionPoints] = useState<
    CollectionPoint[]
  >([]);
  const [availableServiceMethods, setAvailableSerMethods] = useState<
    ServiceMethod[]
  >([]);
  const [availableDeliveryAreas, setAvailableDeliveryAreas] = useState<
    DeliveryArea[]
  >(venue?.deliveryAreas || []);
  const [draftDeliveryInfo, setDraftDeliveryInfo] =
    useState<IVenueDetailsCaptureFormProps['serviceChoices']['deliveryInfo']>();
  const [isAValidLocationSelection, setLocSelectionValidity] = useState(false);
  const [selectedDeliveryArea, setSelectedDeliveryArea] = useState<string>('');
  const [roomNo, setRoomNo] = useState<string>('');
  const [dineInTableNo, setDineInTableNo] = useState<string>('');
  const [isModalOpen, setModalOpen] = useState(false);
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const [
    {
      section: sectionFromParams,
      row: rowFromParams,
      seat: seatFromParams,
      collectionPoint: collectionPointFromParams,
      serviceMethod: serviceMethodFromParams,
      deliveryArea: deliveryAreaFromParams,
      table: tableFromParams,
      room: roomFromParams,
    },
  ] = useQueryParams([
    'section',
    'serviceMethod',
    'row',
    'seat',
    'collectionPoint',
    'deliveryArea',
    'table',
    'room',
  ]);

  useEffect(() => {
    if (venue) {
      const sections: Section[] =
        venue?.sections.filter((section: Section) => !section.isDeleted) || [];
      setSections(sections);
      // If only one section, default select it
      if (sections.length === 1) {
        onSectionChange(sections[0]);
      }
    } else {
      setSections([]);
      setCollectionPoints([]);
      setDraftDeliveryInfo(undefined);
      setSelectedServiceMethod(undefined);
      setRoomNo('');
      setDineInTableNo('');
    }
  }, [venue, serviceChoices]);

  let isCollectionPointDisabled = false;
  let isDeliveryAreaDisabled = false;

  useEffect(() => {
    // Auto-fill on mount if QR is used
    const section = availableSections.find(
      (section) =>
        section.label.toUpperCase() === sectionFromParams?.trim().toUpperCase(),
    );
    if (!section) return;
    onSectionChange(section);
    if (serviceMethodFromParams?.trim()) {
      setSelectedServiceMethod(
        serviceMethodFromParams?.trim().toUpperCase() as ServiceMethod,
      );
    }

    if (serviceMethodFromParams) {
      switch (serviceMethodFromParams?.trim().toUpperCase()) {
        case ServiceMethod.COLLECTION_POINT: {
          const collectionPoint = availableCollectionPoints.find((cp) => {
            const result =
              !cp.isDeleted &&
              cp.label.toLowerCase() ===
                collectionPointFromParams?.trim().toLowerCase();
            isCollectionPointDisabled = result ? true : false;
            return result;
          });
          onCollectionPointChange(collectionPoint?.id || '');
          break;
        }
        case ServiceMethod.DELIVERY: {
          onRowSeatChange(
            rowFromParams?.trim() || '',
            seatFromParams?.trim() || '',
          );
          break;
        }
        case ServiceMethod.VENDOR_DELIVERY: {
          const deliveryArea = availableDeliveryAreas.find((da) => {
            const result =
              !da.isDeleted &&
              da.label.toLowerCase() ===
                deliveryAreaFromParams?.trim().toLowerCase();
            isDeliveryAreaDisabled = result ? true : false;
            return result;
          });
          onDeliveryAreaChange(deliveryArea?.id || '');
          break;
        }
        case ServiceMethod.ROOM_SERVICE: {
          setRoomNo(roomFromParams?.trim() || '');
          break;
        }
        case ServiceMethod.TABLE_SERVICE: {
          setDineInTableNo(tableFromParams?.trim() || '');
          break;
        }
      }
    }
  }, [venue, serviceChoices]);

  useEffect(() => {
    let isValid = false;

    switch (selectedServiceMethod) {
      case ServiceMethod.DELIVERY:
        isValid = !!draftDeliveryInfo?.row && !!draftDeliveryInfo?.seat;
        break;
      case ServiceMethod.COLLECTION_POINT:
        isValid = !!draftDeliveryInfo?.collectionPoint;
        break;
      case ServiceMethod.ROOM_SERVICE:
        isValid = roomNo.trim() !== '';
        break;
      case ServiceMethod.TABLE_SERVICE:
        isValid = dineInTableNo.trim() !== '';
        break;
      case ServiceMethod.COLLECT_FROM_VENDOR:
        isValid = true;
        break;
      case ServiceMethod.VENDOR_DELIVERY:
        isValid = !!selectedDeliveryArea;
        break;
    }
    setLocSelectionValidity(isValid);
  }, [
    draftDeliveryInfo,
    selectedServiceMethod,
    selectedDeliveryArea,
    roomNo,
    dineInTableNo,
    availableSections,
  ]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const allStoresClosed = vendors.every((vendor) => !vendor.isOpen);
      if (allStoresClosed) {
        await refetchVendor();
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, []);

  const prepareSectionAvailabilities = (section: Section) => {
    const sectionServiceMethods = section?.serviceMethods || [];
    let sectionCollectionPoints: CollectionPoint[] = [];
    sectionCollectionPoints =
      section && venue.collectionPoints
        ? [
            ...venue.collectionPoints.filter(
              (cp) =>
                !cp.isDeleted &&
                cp.serviceableSectionsIds?.includes(section.id),
            ),
          ]
        : [];
    let sectionDeliveryAreas: DeliveryArea[] = [];
    sectionDeliveryAreas =
      section && venue.deliveryAreas
        ? [
            ...venue.deliveryAreas.filter(
              (da) =>
                !da.isDeleted &&
                da.serviceableSectionsIds?.includes(section.id),
            ),
          ]
        : [];
    setCollectionPoints(sectionCollectionPoints);
    setAvailableDeliveryAreas(sectionDeliveryAreas);
    setAvailableSerMethods(sectionServiceMethods);
    // If only one service method, set it by default
    if (sectionServiceMethods.length === 1) {
      setSelectedServiceMethod(sectionServiceMethods[0]);
    }
  };

  const onSectionChange = (section: Section) => {
    prepareSectionAvailabilities(section);
    setDraftDeliveryInfo({
      section: section.id,
      collectionPoint: undefined,
      row: undefined,
      seat: undefined,
      deliveryArea: undefined,
    });
    setDineInTableNo('');
    setSelectedDeliveryArea('');
    setRoomNo('');
    setDineInTableNo('');
  };

  const onServiceMethodChange = (serviceMethod: ServiceMethod) => {
    setSelectedServiceMethod(serviceMethod);
    // If switching to COLLECTION_POINT or DELIVEY AREA, refresh collection points and delivery area for the selected section
    if (
      (serviceMethod === ServiceMethod.COLLECTION_POINT ||
        serviceMethod === ServiceMethod.VENDOR_DELIVERY) &&
      draftDeliveryInfo?.section
    ) {
      const selectedSection = availableSections.find(
        (s) => s.id === draftDeliveryInfo.section,
      );
      if (selectedSection) {
        prepareSectionAvailabilities(selectedSection);
      }
    }
  };

  const onCollectionPointChange = (cpID: string) => {
    if (draftDeliveryInfo) {
      const cp = availableCollectionPoints.find((cp) => cp.id === cpID);
      if (cp) {
        setSelectedServiceMethod(ServiceMethod.COLLECTION_POINT);
        setDraftDeliveryInfo({
          ...draftDeliveryInfo,
          collectionPoint: cp.id,
        });
      }
    }
  };

  const onRowSeatChange = (row = '', seat = '') => {
    if (draftDeliveryInfo) {
      setSelectedServiceMethod(ServiceMethod.DELIVERY);
      setDraftDeliveryInfo({ ...draftDeliveryInfo, row, seat });
    }
  };

  const onDeliveryAreaChange = (area: string) => {
    setSelectedDeliveryArea(area);
    setSelectedServiceMethod(ServiceMethod.VENDOR_DELIVERY);
  };

  const getServiceMethodsOptions = () => {
    const defaultMethodOptions = [
      {
        id: ServiceMethod.DELIVERY as string,
        label: t('patron_portal.seatSelectionForm.ttsOptionMessage', {
          ns: 'patron_portal',
        }),
      },
      {
        id: ServiceMethod.COLLECT_FROM_VENDOR as string,
        label: t('patron_portal.seatSelectionForm.cncOptionMessage', {
          ns: 'patron_portal',
        }),
      },
      {
        id: ServiceMethod.COLLECTION_POINT as string,
        label: t('patron_portal.seatSelectionForm.cpOptionMessage', {
          ns: 'patron_portal',
        }),
      },
      {
        id: ServiceMethod.VENDOR_DELIVERY as string,
        label: t(
          'patron_portal.seatSelectionForm.vendorDeliveryOptionMessage',
          {
            ns: 'patron_portal',
          },
        ),
      },
      {
        id: ServiceMethod.TABLE_SERVICE as string,
        label: t('patron_portal.seatSelectionForm.tableServiceOptionMessage', {
          ns: 'patron_portal',
        }),
      },
      {
        id: ServiceMethod.ROOM_SERVICE as string,
        label: t('patron_portal.seatSelectionForm.roomServiceOptionMessage', {
          ns: 'patron_portal',
        }),
      },
    ];
    return defaultMethodOptions.filter((option) =>
      availableServiceMethods.includes(option.id as ServiceMethod),
    );
  };

  const handleSectionChange = (value: string) => {
    const section = availableSections.find((s) => s.id === value);
    if (section) onSectionChange(section);
  };

  const onLetsGo = () => {
    if (selectedServiceMethod) {
      let deliveryInfo: IVenueSectionDeliveryInfoMeta = {
        section: draftDeliveryInfo?.section,
      };
      switch (selectedServiceMethod) {
        case ServiceMethod.DELIVERY:
          deliveryInfo = {
            ...deliveryInfo,
            row: draftDeliveryInfo?.row,
            seat: draftDeliveryInfo?.seat,
          };
          break;
        case ServiceMethod.COLLECTION_POINT:
          deliveryInfo = {
            ...deliveryInfo,
            collectionPoint: draftDeliveryInfo?.collectionPoint,
          };
          break;
        case ServiceMethod.VENDOR_DELIVERY:
          if (selectedDeliveryArea) {
            deliveryInfo = {
              ...deliveryInfo,
              deliveryArea: selectedDeliveryArea,
            };
          }
          break;
        case ServiceMethod.ROOM_SERVICE:
          deliveryInfo = {
            ...deliveryInfo,
            seat: roomNo,
          };
          break;
        case ServiceMethod.TABLE_SERVICE:
          deliveryInfo = {
            ...deliveryInfo,
            row: 'N/A',
            seat: dineInTableNo,
          };
          break;
        case ServiceMethod.COLLECT_FROM_VENDOR:
          deliveryInfo = {
            ...deliveryInfo,
            row: 'N/A',
            seat: '0',
          };
          break;
      }
      if (selectedServiceMethod === ServiceMethod.VENDOR_DELIVERY) {
        onLocationSelection(selectedServiceMethod, deliveryInfo);
        handleOpenModal();
      }
      onLocationSelection(selectedServiceMethod, deliveryInfo);
    }
  };

  return (
    <>
      {/* Only show section select if more than one section */}
      {availableSections.length > 1 && (
        <CustomSelect
          onChange={handleSectionChange}
          data={availableSections}
          title={
            appContext.whitelabelConfig?.value?.venueSettings?.sectionLabel ||
            t('patron_portal.seatSelectionForm.stadiumSection', {
              ns: 'patron_portal',
            })
          }
          value={draftDeliveryInfo?.section}
          disabled={!!sectionFromParams?.trim()}
        />
      )}
      {/* Only show service method select if more than one option */}
      {availableServiceMethods.length > 1 ? (
        <CustomSelect
          onChange={onServiceMethodChange}
          disabled={!!serviceMethodFromParams?.trim()}
          data={getServiceMethodsOptions()}
          title={t(
            'patron_portal.seatSelectionForm.preferredServiceMethodLabel',
            {
              ns: 'patron_portal',
            },
          )}
          value={selectedServiceMethod}
        />
      ) : null}
      {selectedServiceMethod === ServiceMethod.COLLECTION_POINT && (
        <CustomSelect
          onChange={(value: string) => {
            onCollectionPointChange(value);
          }}
          data={availableCollectionPoints || []}
          title={t('patron_portal.seatSelectionForm.collectionPoint', {
            ns: 'patron_portal',
          })}
          value={draftDeliveryInfo?.collectionPoint}
          disabled={isCollectionPointDisabled}
        />
      )}
      {selectedServiceMethod === ServiceMethod.DELIVERY && (
        <>
          <CustomInput
            onChange={(value: string) => {
              onRowSeatChange(value, draftDeliveryInfo?.seat || '');
            }}
            title={t('common.row', { ns: 'common' })}
            value={draftDeliveryInfo?.row}
            disabled={!!rowFromParams?.trim()}
          />
          <CustomInput
            onChange={(value: string) => {
              onRowSeatChange(draftDeliveryInfo?.row || '', value);
            }}
            title={t('common.seat', { ns: 'common' })}
            value={draftDeliveryInfo?.seat}
            disabled={!!seatFromParams?.trim()}
          />
        </>
      )}
      {selectedServiceMethod === ServiceMethod.VENDOR_DELIVERY && (
        <Stack>
          <CustomSelect
            onChange={onDeliveryAreaChange}
            data={availableDeliveryAreas}
            title={t('common.deliveryArea', { ns: 'common' })}
            value={selectedDeliveryArea}
            disabled={isDeliveryAreaDisabled}
          />
          <LocationDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            onSaveAndContinue={() => {
              navigate(`${STORES_LIST_ROUTE}`);
            }}
          />
        </Stack>
      )}
      {selectedServiceMethod === ServiceMethod.ROOM_SERVICE && (
        <CustomInput
          onChange={setRoomNo}
          title={t('common.roomNo', { ns: 'common' })}
          value={roomNo}
        />
      )}
      {selectedServiceMethod === ServiceMethod.TABLE_SERVICE && (
        <CustomInput
          onChange={setDineInTableNo}
          title={t('common.tableNo', { ns: 'common' })}
          value={dineInTableNo}
        />
      )}
      <Box flexGrow={1} mt={2}>
        <CustomButton
          disabled={!isAValidLocationSelection}
          title={t('patron_portal.seatSelectionForm.submitButtonLabel', {
            ns: 'patron_portal',
          })}
          onClick={onLetsGo}
        />
      </Box>
    </>
  );
};
